import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ACTION_COLORS = {
  resume_parsing: '#00A0E0', candidate_ranking: '#F05000', assessment_generation: '#059669',
  ai_questions: '#D97706', video_interview: '#DC2626',
};
const money = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => (Number(v) || 0).toLocaleString();
const fmtMinute = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso).slice(0, 16).replace('T', ' ');
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

function Kpi({ label, value, hint, tone = '#002A38', big, testId }) {
  return (
    <Card data-testid={testId} style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B' }}>{label}</div>
      <div style={{ fontSize: big ? '2.25rem' : '1.75rem', fontWeight: 800, fontFamily: 'Cabinet Grotesk', color: tone, marginTop: '0.25rem', lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.375rem' }}>{hint}</div>}
    </Card>
  );
}

function StepChip({ s }) {
  const c = ACTION_COLORS[s.action] || '#94A3B8';
  const est = s.link_source === 'backfill';
  return (
    <div data-testid="journey-step" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 10, background: `${c}0F`, border: `1px solid ${c}33` }}>
      <span style={{ width: 8, height: 8, borderRadius: 999, background: c, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#002A38' }}>
          {s.label} <span style={{ color: '#F05000' }}>+{num(s.credits)} cr</span>
          {est && <span title="Estimated link (backfilled)" style={{ marginLeft: 4, fontSize: '0.55rem', fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '0 0.25rem', borderRadius: 4 }}>~est</span>}
        </div>
        <div style={{ fontSize: '0.625rem', color: '#94A3B8' }}>{fmtMinute(s.created_at)} · {money(s.cost)}</div>
      </div>
    </div>
  );
}

function Journey({ j }) {
  const [open, setOpen] = useState(false);
  const isJob = j.is_job_level;
  const label = j.candidate_name || (isJob ? `Job-level: ${j.job_title || 'Untitled job'}` : (j.job_title ? `Job: ${j.job_title}` : 'Unlinked usage'));
  return (
    <div data-testid="usage-journey" style={{ border: '1px solid #EEF0F5', borderRadius: 12, marginBottom: '0.5rem', background: '#fff' }}>
      <div onClick={() => setOpen((o) => !o)} data-testid="journey-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.6rem 0.85rem', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <span style={{ color: '#94A3B8', fontSize: '0.7rem', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#002A38', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {label}
              {isJob && <span title="Shared across candidates (job-level generation)" style={{ marginLeft: 6, fontSize: '0.55rem', fontWeight: 700, color: '#005A80', background: '#E0F2FE', padding: '0.05rem 0.35rem', borderRadius: 5 }}>SHARED</span>}
              {j.is_unlinked && <span title="No candidate/job context recorded" style={{ marginLeft: 6, fontSize: '0.55rem', fontWeight: 700, color: '#64748B', background: '#F1F5F9', padding: '0.05rem 0.35rem', borderRadius: 5 }}>UNLINKED</span>}
            </div>
            {j.candidate_name && j.job_title && <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{j.job_title}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
          <span style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>{j.actions} steps</span>
          <span style={{ fontSize: '0.875rem', fontWeight: 800, color: '#F05000', fontFamily: 'Cabinet Grotesk' }}>{num(j.credits)} cr</span>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>{money(j.cost)}</span>
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 0.85rem 0.75rem 2rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.5rem' }}>
          {j.steps.map((s, i) => <StepChip key={`${s.action}-${s.created_at || i}`} s={s} />)}
        </div>
      )}
    </div>
  );
}

function Timeline({ tl }) {
  if (!tl) return <div style={{ fontSize: '0.75rem', color: '#94A3B8', padding: '0.5rem' }}>Loading history…</div>;
  if (!tl.events || tl.events.length === 0) return <div style={{ fontSize: '0.75rem', color: '#94A3B8', padding: '0.5rem' }}>No events for this period.</div>;
  return (
    <div data-testid="login-timeline" style={{ overflowX: 'auto' }}>
      <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', margin: '0.5rem 0' }}>
        Minute-level history — {tl.totals?.actions} events · {num(tl.totals?.credits)} credits · {money(tl.totals?.cost)}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: '#64748B', fontSize: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <th style={{ padding: '0.35rem 0.5rem' }}>When</th>
            <th style={{ padding: '0.35rem 0.5rem' }}>Action</th>
            <th style={{ padding: '0.35rem 0.5rem' }}>Candidate / Job</th>
            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>Credits</th>
            <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {tl.events.map((e, i) => {
            const c = ACTION_COLORS[e.action] || '#94A3B8';
            return (
              <tr key={`${e.created_at}-${e.action}-${e.candidate_id || e.job_id || i}`} data-testid="timeline-event" style={{ borderTop: '1px solid #F1F5F9' }}>
                <td style={{ padding: '0.35rem 0.5rem', color: '#334155', whiteSpace: 'nowrap' }}>{fmtMinute(e.created_at)}</td>
                <td style={{ padding: '0.35rem 0.5rem' }}>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: c }}>{e.label}</span>
                  {e.link_source === 'backfill' && <span style={{ marginLeft: 4, fontSize: '0.55rem', fontWeight: 700, color: '#B45309', background: '#FEF3C7', padding: '0 0.25rem', borderRadius: 4 }}>~est</span>}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', color: '#64748B', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {e.candidate_name || (e.job_title ? `Job: ${e.job_title}` : '—')}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#F05000' }}>{num(e.credits)}</td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', color: '#059669' }}>{money(e.cost)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function csvExport(data, scope) {
  const rows = [['Login', 'Company', 'Candidate/Job', 'Journey Type', 'Action', 'Credits', 'Cost (USD)', 'When', 'Link']];
  (data.logins || []).forEach((l) => {
    (l.journeys || []).forEach((j) => {
      const who = j.candidate_name || j.job_title || 'Unlinked';
      const jt = j.is_unlinked ? 'unlinked' : (j.is_job_level ? 'job-level' : 'candidate');
      (j.steps || []).forEach((s) => {
        rows.push([l.recruiter_name, l.company_slug || '', who, jt, s.label, s.credits, (s.cost ?? 0).toFixed(2), s.created_at || '', s.link_source || 'live']);
      });
    });
  });
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `chire-credit-usage-${scope}-${data.month || 'all-time'}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function CreditUsageByLogin({ getReport, getTimeline, onBackfill, showCompany = false, intro }) {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [historyOpen, setHistoryOpen] = useState({});
  const [timelines, setTimelines] = useState({});
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await getReport(m || undefined);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load credit usage');
    } finally {
      setLoading(false);
    }
  }, [getReport]);

  useEffect(() => { load(month); }, [month, load]);

  const loadTimeline = async (rid) => {
    setHistoryOpen((p) => ({ ...p, [rid]: !p[rid] }));
    if (timelines[rid]) return;
    try {
      const res = await getTimeline(rid, month || undefined);
      setTimelines((p) => ({ ...p, [rid]: res.data }));
    } catch (e) {
      toast.error('Failed to load history');
    }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await onBackfill();
      const r = res.data || {};
      toast.success(`Linked ${r.total || 0} historical events (${r.candidate_linked || 0} candidate · ${r.job_linked || 0} job · ${r.video_linked || 0} interview)`);
      setTimelines({});
      await load(month);
    } catch (e) {
      toast.error('Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading && !data) return <div style={{ color: '#64748B', padding: '2rem' }}>Loading credit usage…</div>;
  if (!data) return null;

  const totals = data.totals || {};
  const logins = data.logins || [];
  const cpc = data.cost_per_credit;

  return (
    <div data-testid="credit-usage-by-login" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <p style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', maxWidth: 560, margin: 0, lineHeight: 1.6 }}>
          {intro || 'Absolute credit usage by login. Expand a login to trace each candidate\u2019s journey — resume parse → ranking → assessment → interview questions → video interview — with credits at every step.'}
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.25rem' }}>Period</label>
            <select data-testid="usage-login-month-select" value={month} onChange={(e) => { setMonth(e.target.value); setTimelines({}); setHistoryOpen({}); }}
              style={{ padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', fontSize: '0.8125rem' }}>
              <option value="">All time</option>
              {(data.months_available || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          {onBackfill && (
            <Button data-testid="usage-backfill-btn" onClick={runBackfill} disabled={backfilling} variant="outline" style={{ borderRadius: 8 }}>
              {backfilling ? 'Linking…' : 'Backfill history links'}
            </Button>
          )}
          <Button data-testid="usage-login-export-csv" onClick={() => csvExport(data, showCompany ? 'admin' : 'team')} variant="outline" style={{ borderRadius: 8 }}>Export CSV</Button>
        </div>
      </div>

      {/* KPIs — absolute credits headline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <Kpi testId="kpi-login-credits" label="Absolute credits" value={num(totals.credits)} tone="#F05000" big hint="total consumed" />
        <Kpi testId="kpi-login-count" label="Logins" value={num(totals.logins)} hint={month || 'all time'} />
        <Kpi testId="kpi-login-actions" label="AI actions" value={num(totals.actions)} />
        <Kpi testId="kpi-login-cost" label="Est. cost" value={money(totals.cost)} tone="#059669" hint={`@ ${money(cpc)}/credit`} />
      </div>

      {/* Per-login table */}
      <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 14, padding: '1.5rem' }}>
        <h3 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Usage by login{month ? ` — ${month}` : ''}</h3>
        {logins.length === 0 ? (
          <p data-testid="usage-login-empty" style={{ fontSize: '0.8125rem', color: '#94A3B8' }}>No credit usage recorded yet for this period.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {logins.map((l) => {
              const rid = l.recruiter_id || '(unknown)';
              const open = !!expanded[rid];
              const hOpen = !!historyOpen[rid];
              return (
                <div key={rid} data-testid="usage-login-row" style={{ border: '1px solid #E9EDF3', borderRadius: 14, overflow: 'hidden' }}>
                  {/* Login header */}
                  <div onClick={() => setExpanded((p) => ({ ...p, [rid]: !p[rid] }))}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.85rem 1rem', cursor: 'pointer', background: open ? '#FAFAFF' : '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
                      <span style={{ color: '#94A3B8', fontSize: '0.75rem', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>▶</span>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#002A38' }}>{l.recruiter_name}</div>
                        <div style={{ fontSize: '0.6875rem', color: '#94A3B8' }}>
                          {showCompany && l.company_slug ? `${l.company_slug} · ` : ''}{l.journeys.length} journeys · {l.actions} actions
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#F05000', fontFamily: 'Cabinet Grotesk', lineHeight: 1 }} data-testid="login-credits-value">{num(l.credits)}</div>
                        <div style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94A3B8' }}>credits</div>
                      </div>
                      <div style={{ textAlign: 'right', minWidth: 64 }}>
                        <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: '#059669' }}>{money(l.cost)}</div>
                        <div style={{ fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#94A3B8' }}>est. cost</div>
                      </div>
                    </div>
                  </div>

                  {open && (
                    <div style={{ padding: '0.25rem 1rem 1rem 1rem', background: '#FAFAFF' }}>
                      {/* Action breakdown chips */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', margin: '0.5rem 0 0.85rem' }}>
                        {Object.entries(l.by_action || {}).map(([at, ba]) => (
                          <span key={at} title={`${ba.label}: ${ba.actions} actions · ${ba.credits} credits`}
                            style={{ fontSize: '0.625rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 999, background: `${ACTION_COLORS[at] || '#94A3B8'}14`, color: ACTION_COLORS[at] || '#64748B' }}>
                            {ba.label} · {num(ba.credits)} cr
                          </span>
                        ))}
                      </div>

                      <div style={{ fontSize: '0.625rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748B', marginBottom: '0.5rem' }}>Candidate journeys</div>
                      {l.journeys.map((j) => <Journey key={j.key} j={j} />)}

                      <Button data-testid="login-history-btn" onClick={() => loadTimeline(rid)} variant="outline" size="sm" style={{ borderRadius: 8, marginTop: '0.5rem' }}>
                        {hOpen ? 'Hide minute-level history' : 'View minute-level history'}
                      </Button>
                      {hOpen && <div style={{ marginTop: '0.75rem', borderTop: '1px solid #EEF0F5', paddingTop: '0.5rem' }}><Timeline tl={timelines[rid]} /></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
